import { useEffect, useState } from 'react';
import { api, ApiError, type PackageSummary, type Visibility } from '../api';
import { useAuth } from '../auth';
import { Alert, Badge, Button, Card, PageHeader, Select, Spinner, Textarea } from '../components/ui';

const TEMPLATE = (github: string) =>
  JSON.stringify(
    {
      id: '@community/my-tool',
      name: 'My Tool',
      version: '1.0.0',
      kind: 'tool',
      platform: 'swarmai',
      trust: 'community',
      description: 'What my tool does, in one sentence.',
      swarmaiVersion: '>=0.5.0',
      downloadUrl: 'https://github.com/me/my-tool/releases/download/v1.0.0/my-tool.tgz',
      checksum: '0000000000000000000000000000000000000000000000000000000000000000',
      repo: 'https://github.com/me/my-tool',
      license: 'MIT',
      author: { name: 'Me', github: github || 'my-github-handle' },
    },
    null,
    2,
  );

export default function Publish() {
  const { user } = useAuth();
  const [pkgs, setPkgs] = useState<PackageSummary[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  // publish form
  const [manifestText, setManifestText] = useState(() => TEMPLATE(user?.github ?? ''));
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState<string | null>(null);

  async function load() {
    try {
      setPkgs(await api.myPackages());
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : 'Failed to load packages');
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function submit() {
    setFormError(null);
    setFieldErrors([]);
    setSuccess(null);
    let manifest: Record<string, unknown>;
    try {
      manifest = JSON.parse(manifestText);
    } catch {
      return setFormError('Manifest is not valid JSON.');
    }
    setBusy(true);
    try {
      const res = await api.publish(manifest, visibility);
      setSuccess(`${res.created ? 'Published' : 'Updated'} ${res.package.id} v${res.package.latestVersion}.`);
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
        const details = err.details as { errors?: string[] } | undefined;
        if (Array.isArray(details?.errors)) setFieldErrors(details!.errors!);
      } else {
        setFormError('Publish failed.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function toggleVisibility(p: PackageSummary) {
    const next: Visibility = p.visibility === 'public' ? 'private' : 'public';
    await api.setVisibility(p.id, next).catch(() => {});
    await load();
  }

  async function yank(p: PackageSummary) {
    if (!confirm(`Yank ${p.id}? It will be removed from the catalogue.`)) return;
    await api.yank(p.id).catch(() => {});
    await load();
  }

  return (
    <div>
      <PageHeader title="My Packages" subtitle="Publish new packages and manage what you've already shipped." />

      {!user?.canPublish && (
        <div className="mb-6">
          <Alert kind="info">
            Your account isn't approved to publish yet. You can prepare a manifest below, but publishing will be
            rejected until an admin enables it.
          </Alert>
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-2">
        {/* ---- publish form ---- */}
        <Card>
          <h3 className="mb-4 font-semibold text-slate-900">Publish or update</h3>
          {formError && (
            <div className="mb-3">
              <Alert kind="error">
                {formError}
                {fieldErrors.length > 0 && (
                  <ul className="mt-2 list-inside list-disc space-y-0.5 text-xs">
                    {fieldErrors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                )}
              </Alert>
            </div>
          )}
          {success && (
            <div className="mb-3">
              <Alert kind="success">{success}</Alert>
            </div>
          )}
          <label className="mb-1 block text-sm font-medium text-slate-700">Manifest (JSON)</label>
          <Textarea rows={18} value={manifestText} onChange={(e) => setManifestText(e.target.value)} spellCheck={false} />
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">Visibility</label>
              <Select value={visibility} onChange={(e) => setVisibility(e.target.value as Visibility)}>
                <option value="public">Public — listed in the catalogue</option>
                <option value="private">Private — only you can see/install</option>
              </Select>
            </div>
            <Button onClick={submit} disabled={busy} className="mt-6">
              {busy ? 'Publishing…' : 'Publish'}
            </Button>
          </div>
          <p className="mt-3 text-xs text-slate-400">
            Need the format? See <code>GET /api/v1</code> (discovery) and <code>GET /api/v1/schema</code>. Remote
            publishing is limited to the <code>community</code> tier.
          </p>
        </Card>

        {/* ---- owned packages ---- */}
        <div>
          <h3 className="mb-3 font-semibold text-slate-900">Published by you</h3>
          {listError && <Alert kind="error">{listError}</Alert>}
          {!pkgs ? (
            <Spinner />
          ) : pkgs.length === 0 ? (
            <p className="text-sm text-slate-500">Nothing published yet.</p>
          ) : (
            <div className="space-y-3">
              {pkgs.map((p) => (
                <Card key={p.id} className="py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-slate-900">{p.name}</span>
                        <Badge color={p.visibility === 'public' ? 'green' : 'amber'}>{p.visibility}</Badge>
                        <Badge>{p.kind}</Badge>
                      </div>
                      <code className="text-xs text-slate-500">
                        {p.id} · v{p.latestVersion}
                      </code>
                      <p className="mt-1 line-clamp-2 text-sm text-slate-500">{p.description}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button variant="secondary" onClick={() => toggleVisibility(p)}>
                      Make {p.visibility === 'public' ? 'private' : 'public'}
                    </Button>
                    <Button variant="danger" onClick={() => yank(p)}>
                      Yank
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
